import { Link, Navigate, useLocation } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { US_STATES } from "@/data/usaStates";
import { USA_HUB } from "@/data/usaPages";
import { stripLocale } from "@/lib/i18n";

/**
 * Shared template for the eight US state-level India sourcing pages
 * (/india-sourcing-new-york, /california, /texas, /florida, /illinois,
 * /georgia, /ohio, /washington). Each page has a UNIQUE H1, meta,
 * sections and FAQ so there is no duplicate content.
 */
export const UsaState = () => {
  const location = useLocation();
  const page = US_STATES.find((p) => p.slug === stripLocale(location.pathname));
  const { open } = useStartProject();

  useSeo({
    title: page ? page.title : "US State programme",
    description: page ? page.metaDescription : undefined,
    path: page ? page.slug : "/usa",
    type: "article",
    jsonLd: page
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}${page.slug}#service`,
              name: page.h1,
              description: page.metaDescription,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: {
                "@type": "AdministrativeArea",
                name: page.state,
                containedInPlace: { "@type": "Country", name: "United States" },
              },
              serviceType: `India sourcing · buying agent · quality control · ${page.state}`,
              url: `${SITE_URL}${page.slug}`,
              isPartOf: {
                "@type": "WebPage",
                "@id": `${SITE_URL}${USA_HUB.slug}`,
                name: USA_HUB.h1,
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}${page.slug}#faq`,
              mainEntity: (page.faqs || []).map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "USA", path: USA_HUB.slug },
              { name: page.state, path: page.slug },
            ]),
          ],
        }
      : null,
    jsonLdId: "usa-state-jsonld",
  });

  if (!page) return <Navigate to="/usa" replace />;

  const idx = US_STATES.findIndex((p) => p.slug === page.slug);
  const prev = US_STATES[(idx - 1 + US_STATES.length) % US_STATES.length];
  const next = US_STATES[(idx + 1) % US_STATES.length];

  return (
    <article data-testid="usa-state-page">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}
      >
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }} data-testid="usa-state-breadcrumb-home">
              Home
            </Link>{" "}
            ·{" "}
            <Link to="/usa" style={{ color: "inherit" }} data-testid="usa-state-breadcrumb-usa">
              USA
            </Link>{" "}
            · {page.state}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="usa-state-eyebrow">
            <span
              aria-hidden="true"
              style={{ marginRight: ".5rem", fontSize: "1.4em", verticalAlign: "middle" }}
            >
              🇺🇸
            </span>
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="usa-state-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="usa-state-lede"
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", marginTop: "2rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="usa-state-cta-primary"
          >
            Start Your Project →
          </button>
          <Link to="/usa" className="btn btn-ghost" data-testid="usa-state-cta-hub">
            Back to US hub
          </Link>
        </Reveal>
      </header>

      {/* KEY FACTS strip */}
      <section
        className="wrap"
        data-testid="usa-state-facts"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {page.keyFacts.map(([k, v], i) => (
            <Reveal key={i}>
              <span
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.35,
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY sections */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        {(page.sections || []).map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
            data-testid={`usa-state-section-${i}`}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {(s.body || []).map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
          </Reveal>
        ))}

        {/* Related services */}
        <Reveal
          as="section"
          style={{ marginTop: "2.6rem" }}
          data-testid="usa-state-related-services"
        >
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
            }}
          >
            Related programmes for {page.state} buyers
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {page.relatedServices.map((r, i) => (
              <Link
                key={i}
                to={r.slug}
                data-testid={`usa-state-related-${i}`}
                style={{
                  display: "block",
                  padding: "1.2rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                  transition: "border-color 200ms, transform 200ms",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .4rem",
                  }}
                >
                  Programme
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  {r.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        data-testid="usa-state-faq"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.01em",
          }}
        >
          Frequently asked questions — {page.state} buyers
        </h2>
        {(page.faqs || []).map(([q, a], i) => (
          <details
            key={i}
            data-testid={`usa-state-faq-${i}`}
            style={{
              borderTop: i === 0 ? "1px solid var(--line)" : "none",
              borderBottom: "1px solid var(--line)",
              padding: "1.2rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                letterSpacing: "-.005em",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: ".8rem 0 0",
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </details>
        ))}
      </section>

      {/* State pagination */}
      <section
        className="wrap"
        data-testid="usa-state-pagination"
        style={{
          paddingBlock: "var(--section)",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap",
          borderTop: "1px solid var(--line)",
        }}
      >
        <Link
          to={prev.slug}
          data-testid="usa-state-prev"
          style={{ color: "var(--ink)", textDecoration: "none" }}
        >
          <p
            className="mono"
            style={{
              color: "var(--brass-deep)",
              fontSize: ".62rem",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            ← Previous state
          </p>
          <p
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.05rem",
              margin: ".3rem 0 0",
              letterSpacing: "-.005em",
            }}
          >
            {prev.state}
          </p>
        </Link>
        <Link
          to={next.slug}
          data-testid="usa-state-next"
          style={{ color: "var(--ink)", textDecoration: "none", textAlign: "right" }}
        >
          <p
            className="mono"
            style={{
              color: "var(--brass-deep)",
              fontSize: ".62rem",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              margin: 0,
            }}
          >
            Next state →
          </p>
          <p
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.05rem",
              margin: ".3rem 0 0",
              letterSpacing: "-.005em",
            }}
          >
            {next.state}
          </p>
        </Link>
      </section>
    </article>
  );
};

export default UsaState;
